import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PMSwitch from "../../utils/PMSwitch";

const DEFAULTS = {
  enabled: false,
  auto_create_case: true,
  auto_create_customer: true,
  auto_reply: false,
  reuse_open_case: true,
  match_phone: true,
  match_name: true,
  ignore_own_domain: false,
  from_domains_ignore: [],
  subject_contains_ignore: [],
  unknown_fallback_to: "",
  system_copy_action: "none",
  system_copy_forward_to: "",
};

const SYSTEM_COPY_ACTIONS = ["none", "archive", "forward"];

export function normalizeInboundEmailSettings(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    ...DEFAULTS,
    ...src,
    from_domains_ignore: Array.isArray(src.from_domains_ignore)
      ? src.from_domains_ignore
      : [],
    subject_contains_ignore: Array.isArray(src.subject_contains_ignore)
      ? src.subject_contains_ignore
      : [],
    unknown_fallback_to: String(src.unknown_fallback_to || "").trim(),
    system_copy_action: SYSTEM_COPY_ACTIONS.includes(String(src.system_copy_action || "").trim())
      ? String(src.system_copy_action).trim()
      : "none",
    system_copy_forward_to: String(src.system_copy_forward_to || "").trim(),
  };
}

export function InboundEmailFallbackFields({ settings, onChange, disabled }) {
  const { t } = useTranslation();
  const current = settings || DEFAULTS;
  const action = current.system_copy_action || "none";

  const patch = (partial) => {
    onChange({ ...current, ...partial });
  };

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
      <div>
        <p className="text-sm font-medium text-gray-900">
          {t("company.inbound_email.fallback_heading")}
        </p>
        <p className="mt-1 text-xs text-gray-600">
          {t("company.inbound_email.fallback_description")}
        </p>
      </div>

      <label className="block text-sm font-medium text-gray-700">
        {t("company.inbound_email.unknown_fallback_to")}
        <input
          type="email"
          autoComplete="email"
          disabled={disabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 sm:text-sm"
          value={current.unknown_fallback_to || ""}
          placeholder={t("company.inbound_email.unknown_fallback_placeholder")}
          onChange={(e) => patch({ unknown_fallback_to: e.target.value })}
        />
        <span className="mt-1 block text-xs font-normal text-gray-500">
          {t("company.inbound_email.unknown_fallback_hint")}
        </span>
      </label>

      <label className="block text-sm font-medium text-gray-700">
        {t("company.inbound_email.system_copy_action")}
        <select
          disabled={disabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 sm:text-sm"
          value={action}
          onChange={(e) => patch({ system_copy_action: e.target.value })}
        >
          {SYSTEM_COPY_ACTIONS.map((value) => (
            <option key={value} value={value}>
              {t(`company.inbound_email.system_copy_action_${value}`)}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-xs font-normal text-gray-500">
          {t("company.inbound_email.system_copy_hint")}
        </span>
      </label>

      {action === "forward" ? (
        <label className="block text-sm font-medium text-gray-700">
          {t("company.inbound_email.system_copy_forward_to")}
          <input
            type="email"
            autoComplete="email"
            disabled={disabled}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 sm:text-sm"
            value={current.system_copy_forward_to || ""}
            placeholder={t("company.inbound_email.system_copy_forward_placeholder")}
            onChange={(e) => patch({ system_copy_forward_to: e.target.value })}
          />
        </label>
      ) : null}
    </div>
  );
}

function listToText(list) {
  return Array.isArray(list) ? list.join(", ") : "";
}

function textToList(text) {
  return String(text || "")
    .split(/[\n,]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

export function InboundEmailSettingsSection({ formik }) {
  const { t } = useTranslation();
  const settings = formik.values.inbound_email_settings || DEFAULTS;

  const setSetting = (key, value) => {
    formik.setFieldValue("inbound_email_settings", {
      ...settings,
      [key]: value,
    });
  };

  return (
    <div className="mt-5 overflow-visible rounded-md shadow">
      <div className="space-y-6 rounded-md bg-white py-6 px-4 sm:p-6">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t("company.inbound_email.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("company.inbound_email.description")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <PMSwitch
            label={t("company.inbound_email.enabled")}
            checked={Boolean(settings.enabled)}
            onChange={(e) => setSetting("enabled", e.target.checked)}
          />
          <PMSwitch
            label={t("company.inbound_email.auto_create_case")}
            checked={Boolean(settings.auto_create_case)}
            onChange={(e) => setSetting("auto_create_case", e.target.checked)}
          />
          <PMSwitch
            label={t("company.inbound_email.auto_create_customer")}
            checked={Boolean(settings.auto_create_customer)}
            onChange={(e) => setSetting("auto_create_customer", e.target.checked)}
          />
          <PMSwitch
            label={t("company.inbound_email.auto_reply")}
            checked={Boolean(settings.auto_reply)}
            onChange={(e) => setSetting("auto_reply", e.target.checked)}
          />
          <PMSwitch
            label={t("company.inbound_email.reuse_open_case")}
            checked={Boolean(settings.reuse_open_case)}
            onChange={(e) => setSetting("reuse_open_case", e.target.checked)}
          />
          <PMSwitch
            label={t("company.inbound_email.match_phone")}
            checked={Boolean(settings.match_phone)}
            onChange={(e) => setSetting("match_phone", e.target.checked)}
          />
          <PMSwitch
            label={t("company.inbound_email.match_name")}
            checked={Boolean(settings.match_name)}
            onChange={(e) => setSetting("match_name", e.target.checked)}
          />
          <PMSwitch
            label={t("company.inbound_email.ignore_own_domain")}
            checked={Boolean(settings.ignore_own_domain)}
            onChange={(e) => setSetting("ignore_own_domain", e.target.checked)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-sm font-medium text-gray-700">
            {t("company.inbound_email.from_domains_ignore")}
            <input
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              defaultValue={listToText(settings.from_domains_ignore)}
              onBlur={(e) => setSetting("from_domains_ignore", textToList(e.target.value))}
            />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            {t("company.inbound_email.subject_contains_ignore")}
            <input
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              defaultValue={listToText(settings.subject_contains_ignore)}
              onBlur={(e) => setSetting("subject_contains_ignore", textToList(e.target.value))}
            />
          </label>
        </div>

        <InboundEmailFallbackFields
          settings={settings}
          onChange={(next) => formik.setFieldValue("inbound_email_settings", next)}
        />

        <p className="text-sm text-gray-500">
          {t("company.inbound_email.mailbox_hint")}{" "}
          <Link
            to="/dashboard/company/microsoft"
            className="font-medium text-blue-600 hover:text-blue-700"
          >
            {t("company.inbound_email.mailbox_link")}
          </Link>
        </p>
      </div>
    </div>
  );
}
